import { BUILTIN_ENVIRONMENTS, environmentSchema, type Environment } from './environment';

/**
 * 背景（環境）の部品のデータベース（FR-ENV-002）。空・地面・光・物理の 4 種類の部品を組み合わせて、
 * 1 つの環境にする。組み合わせは、ID（`builtin:environment/mix/<空>/<地面>/<光>/<物理>`）だけで表せるので、
 * 保存データには ID だけを持つ（組み込みの環境と同じ。部品の定義は保存データに含めない）。
 */

/** 部品の共通の項目。 */
export interface EnvironmentPart<T> {
  /** ID（小文字の英数字。組み合わせの ID に使う） */
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly value: T;
}

type SkyValue = {
  readonly top: string;
  readonly horizon: string;
  /** 霧の濃さ（0〜1） */
  readonly fog: number;
};

/** 空の部品。 */
export const SKY_PARTS: readonly EnvironmentPart<SkyValue>[] = [
  {
    id: 'day',
    name: '晴れ',
    description: '青空。霧はなし。',
    value: { top: '#5b9ee1', horizon: '#bfd9f2', fog: 0 },
  },
  {
    id: 'cloudy',
    name: 'くもり',
    description: '灰色の空。遠くがかすむ。',
    value: { top: '#8f9aa6', horizon: '#cfd6dc', fog: 0.3 },
  },
  {
    id: 'dusk',
    name: '夕焼け',
    description: '上は紺色、地平線はオレンジ色。',
    value: { top: '#3b4f8f', horizon: '#f2a35e', fog: 0.1 },
  },
  {
    id: 'night',
    name: '夜空',
    description: '暗い夜空。',
    value: { top: '#070b1f', horizon: '#1d2850', fog: 0.05 },
  },
  {
    id: 'space',
    name: '宇宙',
    description: '真っ暗な空。空気のない場所向け。',
    value: { top: '#000004', horizon: '#04050c', fog: 0 },
  },
];

type GroundValue = {
  readonly material: Environment['terrain']['material'];
  readonly color: string;
};

/** 地面の部品。 */
export const GROUND_PARTS: readonly EnvironmentPart<GroundValue>[] = [
  {
    id: 'grid',
    name: '格子',
    description: '格子の線を引いた地面（物理の確認用）。',
    value: { material: 'grid', color: '#b9c9d8' },
  },
  {
    id: 'grass',
    name: '草原',
    description: '緑の草地。',
    value: { material: 'grass', color: '#5f9a4a' },
  },
  {
    id: 'sand',
    name: '砂漠',
    description: '黄色い砂地。',
    value: { material: 'sand', color: '#d9bf86' },
  },
  {
    id: 'snow',
    name: '雪原',
    description: '白い雪。',
    value: { material: 'snow', color: '#eef2f6' },
  },
  {
    id: 'asphalt',
    name: '舗装',
    description: '灰色のアスファルト。',
    value: { material: 'asphalt', color: '#4a4d52' },
  },
  {
    id: 'sea',
    name: '海',
    description: '青い水面。',
    value: { material: 'sea', color: '#2f6fa8' },
  },
];

type LightValue = {
  readonly timeOfDay: number;
  readonly sunIntensity: number;
  readonly sunAzimuth?: number;
  readonly sunElevation?: number;
};

/** 光（時刻・太陽の強さ）の部品。 */
export const LIGHT_PARTS: readonly EnvironmentPart<LightValue>[] = [
  {
    id: 'morning',
    name: '朝',
    description: '太陽が低く、やわらかい光（7 時）。',
    value: { timeOfDay: 7.5, sunIntensity: 0.9 },
  },
  {
    id: 'noon',
    name: '昼',
    description: '太陽が高く、明るい光（12 時）。',
    value: { timeOfDay: 12, sunIntensity: 1 },
  },
  {
    id: 'evening',
    name: '夕方',
    description: '太陽が低く、赤みのある光（17 時）。',
    value: { timeOfDay: 17, sunIntensity: 0.85 },
  },
  {
    id: 'night',
    name: '夜',
    description: '月明かりのような、暗い光（0 時）。',
    value: { timeOfDay: 0, sunIntensity: 0.2 },
  },
];

/** 太陽を自分で決めた光の設定。 */
export interface CustomLight {
  /** 方角 [度]（0 = 北、90 = 東、180 = 南）。0〜359 */
  readonly azimuth: number;
  /** 高さ [度]（0 = 地平線、90 = 真上）。0〜90 */
  readonly elevation: number;
  /** 強さ [%]（100 = 標準）。0〜300 */
  readonly intensity: number;
}

/** 太陽を自分で決めた光の部品の ID の接頭辞。 */
const CUSTOM_LIGHT_PREFIX = 'sun-';

/** 太陽を自分で決めた光の部品の ID（値は整数に丸め、範囲に収める）。 */
export function customLightId(light: CustomLight): string {
  const clamp = (value: number, max: number): number =>
    Math.min(max, Math.max(0, Math.round(Number.isFinite(value) ? value : 0)));
  const azimuth = ((Math.round(light.azimuth) % 360) + 360) % 360;
  return `${CUSTOM_LIGHT_PREFIX}${String(azimuth)}-${String(clamp(light.elevation, 90))}-${String(clamp(light.intensity, 300))}`;
}

/** 光の部品の ID が、太陽を自分で決めたものなら、その設定を返す。 */
export function parseCustomLightId(id: string): CustomLight | undefined {
  const match = /^sun-(\d{1,3})-(\d{1,2})-(\d{1,3})$/.exec(id);
  if (!match) return undefined;
  const light = {
    azimuth: Number(match[1]),
    elevation: Number(match[2]),
    intensity: Number(match[3]),
  };
  if (light.azimuth > 359 || light.elevation > 90 || light.intensity > 300) return undefined;
  return light;
}

/** 方角 [度] を、8 方位の名前にする。 */
export function compassName(azimuth: number): string {
  const names = ['北', '北東', '東', '南東', '南', '南西', '西', '北西'];
  return names[Math.round((((azimuth % 360) + 360) % 360) / 45) % 8] ?? '北';
}

/** 光の部品を探す（用意した光、または太陽を自分で決めた光）。 */
export function findLightPart(id: string): EnvironmentPart<LightValue> | undefined {
  const preset = findPart(LIGHT_PARTS, id);
  if (preset) return preset;
  const custom = parseCustomLightId(id);
  if (!custom) return undefined;
  return {
    id,
    name: `太陽 ${compassName(custom.azimuth)}・高さ ${String(custom.elevation)}°`,
    description: `太陽の方角 ${String(custom.azimuth)}°（${compassName(custom.azimuth)}）、高さ ${String(custom.elevation)}°、強さ ${String(custom.intensity)}%。`,
    value: {
      timeOfDay: 12,
      sunIntensity: custom.intensity / 100,
      sunAzimuth: custom.azimuth,
      sunElevation: custom.elevation,
    },
  };
}

type PhysicsValue = Environment['physics'];

/** 物理（重力・空気）の部品。選ぶと、走行の計算が変わる。 */
export const PHYSICS_PARTS: readonly EnvironmentPart<PhysicsValue>[] = [
  {
    id: 'earth',
    name: '地球',
    description: '重力 9.81 m/s²、空気密度 1.225 kg/m³。',
    value: { gravity: 9.80665, airDensity: 1.225, wind: [0, 0, 0] },
  },
  {
    id: 'moon',
    name: '月',
    description: '重力が地球の約 1/6（1.62 m/s²）、空気なし。',
    value: { gravity: 1.62, airDensity: 0, wind: [0, 0, 0] },
  },
  {
    id: 'mars',
    name: '火星',
    description: '重力 3.71 m/s²、空気はごくうすい（0.02 kg/m³）。',
    value: { gravity: 3.71, airDensity: 0.02, wind: [0, 0, 0] },
  },
  {
    id: 'jupiter',
    name: '木星',
    description: '重力が地球の約 2.5 倍（24.8 m/s²）、空気は 0.16 kg/m³。',
    value: { gravity: 24.79, airDensity: 0.16, wind: [0, 0, 0] },
  },
];

/** 4 種類の部品の選択（部品の ID）。 */
export interface EnvironmentParts {
  readonly sky: string;
  readonly ground: string;
  readonly light: string;
  readonly physics: string;
}

/** 組み合わせの ID の接頭辞。 */
const MIX_PREFIX = 'builtin:environment/mix/';

/** 部品の選択から、環境の ID を作る。 */
export function mixEnvironmentId(parts: EnvironmentParts): string {
  return `${MIX_PREFIX}${parts.sky}/${parts.ground}/${parts.light}/${parts.physics}`;
}

const findPart = <T>(
  list: readonly EnvironmentPart<T>[],
  id: string,
): EnvironmentPart<T> | undefined => list.find((part) => part.id === id);

/** 組み合わせの ID から、部品の選択を取り出す（部品が見つからないとき、組み合わせの ID でないときは `undefined`）。 */
export function parseMixEnvironmentId(id: string): EnvironmentParts | undefined {
  if (!id.startsWith(MIX_PREFIX)) return undefined;
  const [sky, ground, light, physics, ...rest] = id.slice(MIX_PREFIX.length).split('/');
  if (sky === undefined || ground === undefined || light === undefined || physics === undefined) {
    return undefined;
  }
  if (rest.length > 0) return undefined;
  if (
    findPart(SKY_PARTS, sky) === undefined ||
    findPart(GROUND_PARTS, ground) === undefined ||
    findLightPart(light) === undefined ||
    findPart(PHYSICS_PARTS, physics) === undefined
  ) {
    return undefined;
  }
  return { sky, ground, light, physics };
}

const composed = new Map<string, Environment>();

/**
 * 部品の選択から、環境を作る（同じ選択からは、同じオブジェクトを返す）。
 * 部品が見つからなければ `undefined`。
 */
export function composeEnvironment(parts: EnvironmentParts): Environment | undefined {
  const id = mixEnvironmentId(parts);
  const cached = composed.get(id);
  if (cached) return cached;
  const sky = findPart(SKY_PARTS, parts.sky);
  const ground = findPart(GROUND_PARTS, parts.ground);
  const light = findLightPart(parts.light);
  const physics = findPart(PHYSICS_PARTS, parts.physics);
  if (!sky || !ground || !light || !physics) return undefined;
  const environment = environmentSchema.parse({
    id,
    name: `${sky.name}・${ground.name}・${light.name}・${physics.name}`,
    description: `空：${sky.name}／地面：${ground.name}／光：${light.name}／物理：${physics.name}`,
    terrain: {
      type: 'flat',
      size: 1000,
      material: ground.value.material,
      color: ground.value.color,
    },
    sky: { type: 'gradient', top: sky.value.top, horizon: sky.value.horizon },
    fog: { density: sky.value.fog },
    lighting: light.value,
    weather: { type: 'clear' },
    physics: physics.value,
    objects: [],
  });
  composed.set(id, environment);
  return environment;
}

/** 環境の ID が組み合わせなら、組み合わせた環境を返す。 */
export function findMixEnvironment(id: string): Environment | undefined {
  const parts = parseMixEnvironmentId(id);
  return parts ? composeEnvironment(parts) : undefined;
}

/** おすすめの組み合わせ（部品を 1 つずつ選ばなくても、選べる）。 */
export interface EnvironmentTemplate {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly parts: EnvironmentParts;
}

/** おすすめの組み合わせ。 */
export const ENVIRONMENT_TEMPLATES: readonly EnvironmentTemplate[] = [
  {
    id: 'meadow',
    name: '草原の昼',
    description: '青空の下の、緑の草原。',
    parts: { sky: 'day', ground: 'grass', light: 'noon', physics: 'earth' },
  },
  {
    id: 'seaside',
    name: '夕焼けの海',
    description: '夕焼けに染まる海。',
    parts: { sky: 'dusk', ground: 'sea', light: 'evening', physics: 'earth' },
  },
  {
    id: 'night-park',
    name: '夜の遊園地',
    description: '夜空と、暗い舗装の広場。',
    parts: { sky: 'night', ground: 'asphalt', light: 'night', physics: 'earth' },
  },
  {
    id: 'desert',
    name: '砂漠',
    description: '強い日ざしの砂漠。',
    parts: { sky: 'day', ground: 'sand', light: 'noon', physics: 'earth' },
  },
  {
    id: 'snowfield',
    name: '雪原の朝',
    description: 'くもり空の下の、白い雪原。',
    parts: { sky: 'cloudy', ground: 'snow', light: 'morning', physics: 'earth' },
  },
  {
    id: 'moon',
    name: '月面',
    description: '重力が 1/6 で、空気のない、宇宙の空。',
    parts: { sky: 'space', ground: 'sand', light: 'noon', physics: 'moon' },
  },
  {
    id: 'mars',
    name: '火星',
    description: '重力 3.71 m/s² の、赤茶けた大地（砂地）。',
    parts: { sky: 'dusk', ground: 'sand', light: 'evening', physics: 'mars' },
  },
];

/** 組み込みの環境（物理の確認用の「テスト空間」と、昔の「月面」）を、部品の選択で表したもの。 */
const BUILTIN_PARTS: Readonly<Record<string, EnvironmentParts>> = {
  'builtin:environment/test-space': { sky: 'day', ground: 'grid', light: 'noon', physics: 'earth' },
  'builtin:environment/moon': { sky: 'day', ground: 'grid', light: 'noon', physics: 'moon' },
};

/**
 * 環境の ID から、部品の選択を求める（組み合わせの ID・組み込みの環境）。
 * ユーザー定義の環境など、部品で表せないものは `undefined`。
 */
export function partsOfEnvironment(id: string): EnvironmentParts | undefined {
  return parseMixEnvironmentId(id) ?? BUILTIN_PARTS[id];
}

/** 選択がおすすめの組み合わせのどれかと同じなら、そのおすすめを返す。 */
export function templateOfParts(parts: EnvironmentParts): EnvironmentTemplate | undefined {
  return ENVIRONMENT_TEMPLATES.find(
    (template) =>
      template.parts.sky === parts.sky &&
      template.parts.ground === parts.ground &&
      template.parts.light === parts.light &&
      template.parts.physics === parts.physics,
  );
}

/**
 * ID から環境を探す。組み込み → 部品の組み合わせ（`builtin:environment/mix/...`）→
 * プロジェクトのユーザー定義の順に探す。見つからなければ `undefined`。
 */
export function findEnvironment(
  id: string,
  userEnvironments: readonly Environment[] = [],
): Environment | undefined {
  return (
    BUILTIN_ENVIRONMENTS.find((e) => e.id === id) ??
    findMixEnvironment(id) ??
    userEnvironments.find((e) => e.id === id)
  );
}
